// Bot-tool invocation audit — fans out audit-ai-agent for a sample of
// recent sessions server-side (via summary Lambda) and aggregates
// tool-invocation + latency + grounding metrics per bot.
//
// Separate button rather than auto-fire because the fan-out is slow
// (OPS rate-limited at ~1 req / 2s): sampleSize=25 ≈ 55s.

import React, { useCallback, useState } from 'react';
import { Alert, Badge, Button, Group, Loader, NumberInput, Progress, Stack, Text, Tooltip } from '@mantine/core';
import { IconAlertTriangle, IconHammer, IconRefresh } from '@tabler/icons-react';

import { useAuth } from '../../../../../shared/contexts/AuthContext';
import { fetchReport, isWxccAiOpsBackendConfigured } from '../../../api_scripts/wxccAiOpsClient';
import { ReportTable } from './ReportTable';
import { fmtTs } from '../utils';

const REPORT_ID = 'bot-tool-audit';

const COLUMNS = [
  {
    id: 'name',
    label: 'Bot',
    pinned: true,
    render: (r) => <Text size="sm" fw={500}>{r.agentName || <em>(unnamed)</em>}</Text>,
  },
  {
    id: 'sampledCount',
    label: 'Sampled',
    hint: 'Sessions from this bot that made it into the sample.',
    align: 'right',
    render: (r) => <Text size="sm">{r.sampledCount}</Text>,
  },
  {
    id: 'botTurns',
    label: 'Bot turns',
    hint: 'Total bot turns across all sampled sessions.',
    align: 'right',
    render: (r) => <Text size="sm">{r.totalBotTurns ?? '—'}</Text>,
  },
  {
    id: 'ungrounded',
    label: 'Ungrounded',
    hint: 'Percent of bot turns that lacked a KB/tool grounding — a hallucination risk indicator.',
    align: 'right',
    render: (r) => <UngroundedBadge pct={r.ungroundedPct} />,
  },
  {
    id: 'errors',
    label: 'Errors',
    hint: 'observedErrors summed across the sample.',
    align: 'right',
    render: (r) => (
      <Badge size="sm" color={r.totalErrors > 0 ? 'red' : 'gray'} variant={r.totalErrors > 0 ? 'filled' : 'light'}>
        {r.totalErrors}
      </Badge>
    ),
  },
  {
    id: 'meanLatency',
    label: 'Avg latency',
    hint: 'Mean of per-session mean latency across the sample.',
    align: 'right',
    render: (r) => <Text size="sm">{r.meanLatencyMs != null ? `${(r.meanLatencyMs / 1000).toFixed(2)}s` : '—'}</Text>,
  },
  {
    id: 'maxLatency',
    label: 'Max latency',
    hint: 'Highest per-session max latency seen in the sample.',
    align: 'right',
    render: (r) => <Text size="sm">{r.maxLatencyMs != null ? `${(r.maxLatencyMs / 1000).toFixed(2)}s` : '—'}</Text>,
  },
  {
    id: 'smallTalkRate',
    label: 'Small-talk',
    hint: 'Router smallTalkRatePct averaged across the sample. High = router under-matching real asks.',
    align: 'right',
    render: (r) => <RoutingBadge pct={r.routing?.smallTalkRatePct} highIsBad />,
  },
  {
    id: 'agentSelectedRate',
    label: 'Agent-selected',
    hint: 'Router agentSelectedRatePct averaged across the sample. Low = router rarely picks a task bot.',
    align: 'right',
    render: (r) => <RoutingBadge pct={r.routing?.agentSelectedRatePct} highIsBad={false} />,
  },
  {
    id: 'classifierCalls',
    label: 'Classifier calls',
    align: 'right',
    render: (r) => <Text size="sm">{r.routing?.classifierCalls ?? '—'}</Text>,
  },
  {
    id: 'topTools',
    label: 'Top tools',
    hint: 'Tools the bot invoked most often, from the groundingTools audit field.',
    wrap: true,
    render: (r) =>
      r.topTools?.length > 0 ? (
        <Group gap={4}>
          {r.topTools.slice(0, 5).map((t) => (
            <Tooltip key={t.name} label={`${t.count} invocation${t.count === 1 ? '' : 's'}`}>
              <Badge size="xs" variant="light">{t.name} · {t.count}</Badge>
            </Tooltip>
          ))}
        </Group>
      ) : (
        <Text size="xs" c="dimmed">—</Text>
      ),
  },
  {
    id: 'auditErrors',
    label: 'Audit errors',
    hint: 'Sessions where audit-ai-agent itself failed.',
    align: 'right',
    render: (r) => (
      <Badge size="sm" color={r.auditErrors > 0 ? 'orange' : 'gray'} variant="light">{r.auditErrors}</Badge>
    ),
  },
];

const DEFAULT_VISIBLE = ['sampledCount', 'botTurns', 'ungrounded', 'errors', 'meanLatency', 'smallTalkRate', 'topTools'];

export function BotToolAudit({ from, to }) {
  const { getApiToken } = useAuth();
  const configured = isWxccAiOpsBackendConfigured();
  const [sampleSize, setSampleSize] = useState(25);
  const [state, setState] = useState({ loading: false, report: null, error: null });

  const runReport = useCallback(async () => {
    setState({ loading: true, report: null, error: null });
    try {
      const report = await fetchReport({ report: 'bot-tool-audit', from, to, sampleSize }, getApiToken);
      setState({ loading: false, report, error: null });
    } catch (e) {
      setState({ loading: false, report: null, error: e.message });
    }
  }, [from, to, sampleSize, getApiToken]);

  if (!configured) {
    return (
      <Alert color="gray" variant="light">
        CC Utils backend not configured — this report requires server-side fan-out and can't run on the frontend alone.
      </Alert>
    );
  }

  if (state.loading) {
    return (
      <Stack gap="xs" align="center" py="xl">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">
          Fetching audit for up to {sampleSize} sessions. Rate-limited ~1 req / 2s —
          this can take {Math.round((sampleSize * 2.2))}s.
        </Text>
      </Stack>
    );
  }

  if (state.error) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={16} />} title="Report failed">
        <Text size="xs">{state.error}</Text>
        <Button size="xs" mt="xs" leftSection={<IconRefresh size={14} />} onClick={runReport}>Retry</Button>
      </Alert>
    );
  }

  if (!state.report) {
    return (
      <Alert color="blue" variant="light" icon={<IconHammer size={16} />}>
        <Stack gap="xs">
          <Text size="sm">
            Expensive report — serialized audit calls, ~{Math.round((sampleSize * 2.2))}s for {sampleSize} sessions. Click Run to start.
          </Text>
          <Group gap="xs" align="flex-end">
            <NumberInput
              label="Sample size"
              value={sampleSize}
              onChange={(v) => setSampleSize(typeof v === 'number' ? v : 25)}
              min={5}
              max={50}
              step={5}
              size="xs"
              style={{ width: 120 }}
            />
            <Button size="xs" leftSection={<IconHammer size={14} />} onClick={runReport}>Run report</Button>
          </Group>
        </Stack>
      </Alert>
    );
  }

  const { bots, sampledSessions, totalSessionsInWindow, generatedAt } = state.report;

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Text size="xs" c="dimmed">
          Sampled {sampledSessions} of {totalSessionsInWindow} sessions · generated {fmtTs(generatedAt, true)}
        </Text>
        <Group gap="xs">
          <NumberInput value={sampleSize} onChange={(v) => setSampleSize(typeof v === 'number' ? v : 25)} min={5} max={50} step={5} size="xs" style={{ width: 100 }} />
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />} onClick={runReport}>Re-run</Button>
        </Group>
      </Group>

      <ReportTable
        title=""
        reportId={REPORT_ID}
        columns={COLUMNS}
        defaultVisibleIds={DEFAULT_VISIBLE}
        rows={bots.map((b) => ({ ...b, key: `${b.agentId}:${b.agentName}` }))}
        emptyLabel="No bots in the sample — try a different window or larger sample size."
      />
    </Stack>
  );
}

function UngroundedBadge({ pct }) {
  if (pct == null) return <Text size="xs" c="dimmed">—</Text>;
  const color = pct > 20 ? 'red' : pct > 10 ? 'orange' : pct > 5 ? 'yellow' : 'teal';
  return (
    <Tooltip label={`${pct}% of bot turns had no KB/tool grounding — hallucination risk if sustained.`}>
      <Group gap={4} wrap="nowrap" justify="flex-end">
        <Badge size="sm" color={color} variant="light">{pct}%</Badge>
        {pct > 0 && <Progress value={pct} size="xs" color={color} style={{ width: 36 }} />}
      </Group>
    </Tooltip>
  );
}

function RoutingBadge({ pct, highIsBad }) {
  if (pct == null) return <Text size="xs" c="dimmed">—</Text>;
  const color = highIsBad
    ? (pct > 50 ? 'red' : pct > 30 ? 'orange' : 'teal')
    : (pct < 30 ? 'red' : pct < 50 ? 'orange' : 'teal');
  return <Badge size="sm" color={color} variant="light">{pct}%</Badge>;
}
